import type { LaborDevShellLockPayload } from "@/lib/labor/laborDevShellLock";
import type { LaborConflictSource, LaborSession } from "@/lib/labor/laborApi";

export type LaborPresenceSource = "server" | "local";

/**
 * Single hub-wide notion of "the user is clocked in" for shell lockdown, SPA handoff, and the account menu hourglass.
 * Server `labor.device_session` wins when present; open mobile task sessions surface as `conflictSource: "mobile"`;
 * otherwise a per-tab local lock (dev or writes-blocked) applies.
 */
export interface LaborPresence {
  page: string;
  /** `null` means the session was started outside the hub (no recorded tab id). */
  tabId: string | null;
  source: LaborPresenceSource;
  /** Set when `source === "server"`; used for `POST /api/labor/end-session`. */
  serverSessionId: number | null;
  /** Hub device session vs mobile `public.labor_sessions` when clocked in elsewhere. */
  conflictSource?: LaborConflictSource;
}

function presenceFromDevShell(devShell: LaborDevShellLockPayload | null): LaborPresence | null {
  if (devShell === null) {
    return null;
  }
  const page = devShell.page.trim();
  if (page === "") {
    return null;
  }
  return {
    page,
    tabId: devShell.tabId,
    source: "local",
    serverSessionId: null,
  };
}

export function resolveLaborPresence(input: {
  openSession: LaborSession | null;
  devShell: LaborDevShellLockPayload | null;
  activeOnOtherPage?: string | null;
  activeOnOtherSource?: LaborConflictSource | null;
}): LaborPresence | null {
  const { openSession, devShell, activeOnOtherPage, activeOnOtherSource } = input;
  if (openSession !== null && openSession.id > 0) {
    const page = (openSession.page ?? "").trim();
    if (page === "") {
      return presenceFromDevShell(devShell);
    }
    return {
      page,
      tabId: openSession.tab_id ?? null,
      source: "server",
      serverSessionId: openSession.id,
      conflictSource: "device",
    };
  }
  const otherPage = (activeOnOtherPage ?? "").trim();
  if (otherPage !== "") {
    return {
      page: otherPage,
      tabId: null,
      source: "server",
      serverSessionId: null,
      conflictSource: activeOnOtherSource === "mobile" ? "mobile" : "device",
    };
  }
  return presenceFromDevShell(devShell);
}
